// Pure bundeling-logica voor het besteloverzicht per leverancier (epic 3, FR-5.1/5.2).
// Groepeert te bestellen regels per product met totaal + herkomst (klant, project, aantal).

export interface PurchaseLine {
  productId: string;
  productName: string;
  clientName: string | null;
  projectName: string;
  projectId: string;
  quantity: number;
}

export interface ProductHerkomst {
  clientName: string | null;
  projectName: string;
  projectId: string;
  quantity: number;
}

export interface ProductTotal {
  productId: string;
  productName: string;
  total: number;
  herkomst: ProductHerkomst[];
}

/**
 * Bundelt inkoopregels per product: één regel per product met het totaal aantal,
 * uitklapbaar naar herkomst per klant/project (FR-5.2). Een klant met meerdere
 * projecten verschijnt als meerdere herkomst-regels.
 */
export function bundleBySupplierProduct(lines: PurchaseLine[]): ProductTotal[] {
  const byProduct = new Map<string, ProductTotal>();
  for (const line of lines) {
    let entry = byProduct.get(line.productId);
    if (!entry) {
      entry = { productId: line.productId, productName: line.productName, total: 0, herkomst: [] };
      byProduct.set(line.productId, entry);
    }
    entry.total += line.quantity;
    entry.herkomst.push({
      clientName: line.clientName,
      projectName: line.projectName,
      projectId: line.projectId,
      quantity: line.quantity,
    });
  }
  return [...byProduct.values()].sort((a, b) => a.productName.localeCompare(b.productName));
}

export interface OrderQtyLine {
  totalUnitsOrdered: number;
  leftoverUnits: number;
  vpe: number;
}

/**
 * Herberekent een inkoopregel nadat `useFromStock` stuks uit voorraad worden gehaald
 * (FR-7.7). De netto-behoefte daalt; de besteleenheden worden opnieuw op VPE afgerond
 * en de leftover (VPE-surplus) volgt daaruit.
 */
export function reduceOrderQty(
  line: OrderQtyLine,
  useFromStock: number
): { totalUnitsOrdered: number; leftoverUnits: number; vpeUnitsOrdered: number; consumed: number } {
  const needed = Math.max(0, line.totalUnitsOrdered - line.leftoverUnits);
  const consumed = Math.max(0, Math.min(useFromStock, needed));
  const newNeeded = needed - consumed;
  const vpe = line.vpe > 0 ? line.vpe : 1;
  const newTotal = newNeeded === 0 ? 0 : Math.ceil(newNeeded / vpe) * vpe;
  return {
    totalUnitsOrdered: newTotal,
    leftoverUnits: newTotal - newNeeded,
    vpeUnitsOrdered: line.vpe > 0 ? newTotal / vpe : newTotal,
    consumed,
  };
}
